<script setup lang="ts">
import StudySelector from './StudySelector.vue';
import SidebarLink from './SidebarLink.vue';
import { useDashboardStore } from '@/stores/dashboard';
import type { SidebarLinkModel } from './interfaces/SidebarLinkModel';
import { computed, onMounted, ref } from 'vue';
import { SidebarLinkIdentifiers } from './LinkIdentifiers';
import { useUserStore } from '@/stores/user';
import { routeConstants } from '@/utils/constants';
import { DateTime } from 'luxon';
import { useStudyStore } from '@/stores/study';

const studyStore = useStudyStore();
const dashboardStore = useDashboardStore();
const userStore = useUserStore();

const newStudyLink: SidebarLinkModel = {
  id: SidebarLinkIdentifiers.NEW_STUDY,
  label: 'New Study',
  icon: 'plus-circle',
  iconStyle: 'fal',
  selected: false,
  route: routeConstants.STUDY_CREATE.name,
  routeEnabled: true
};

const lastLoginDisplay = ref<string | null>(null);
const showProfileOptions = ref(false);

const profileClick = () => {
  showProfileOptions.value = !showProfileOptions.value;
};

onMounted(() => {
  const lastLoginRaw = localStorage.getItem('lastLoginDisplay');
  if (lastLoginRaw) {
    const dt = DateTime.fromISO(lastLoginRaw);
    lastLoginDisplay.value = dt.toLocaleString(DateTime.DATETIME_MED);
  }
});

const part11Version = computed(() => {
  const selectedStudy = studyStore.selectedStudy;
  if (selectedStudy?.flags?.cfrPart11) {
    return `SP data ${selectedStudy.flags.appVersion}`;
  }
  return '';
});
</script>

<template>
  <div class="sidebar">
    <div class="sidebar-logo">
      <img src="@/assets/SpData.svg" alt="SP Logo" class="logo" />
    </div>
    <hr />
    <Suspense><StudySelector /></Suspense>
    <hr />
    <div class="links">
      <div>
        <div v-for="link in dashboardStore.sidebarLinks" :key="link.id">
          <SidebarLink :link="link"/>
        </div>
      </div>
      <div class="bottom">
        <SidebarLink :link="newStudyLink" />
        <SidebarLink :link="userStore.userLink"
        @linkClick="profileClick"
        :showProfileOptions="showProfileOptions" 
        @outsidePopupClick="profileClick" />
      </div>
    </div>

    <div class="last-login" v-if="lastLoginDisplay">
      Last login: {{ lastLoginDisplay }}
    </div>

    <div class="last-login" v-if="part11Version">
     {{ part11Version }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
}
hr {
  border-color: hsla(0, 0%, 0%, 0.12);
}
.links {
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
}
.bottom {
  margin-top: auto;
}
.last-login {
  padding-left: 32px;
  padding-right: 32px;
  padding-bottom: 16px;
}
.sidebar-logo {
  display: flex;
  justify-content: center;
}
.logo {
  max-width: 260px;
  padding: 20px 8px;
}
</style>
